import { Service } from "dioc"
import { computed, ref, watch } from "vue"
import { useIntervalFn, useLocalStorage } from "@vueuse/core"
import * as E from "fp-ts/Either"
import { runGQLQuery } from "@hoppscotch/common/helpers/backend/GQLClient"
import { useStreamStatic } from "@hoppscotch/common/composables/stream"
import { platform } from "@hoppscotch/common/platform"
import {
  getOrganizationDomain,
  isDefaultCloudInstance,
} from "@app/helpers/utils/cloud-for-orgs"
import {
  GetMyOrganizationsDocument,
  GetMyOrganizationsQuery,
} from "@app/helpers/backend/graphql"
import { validateOrgDomain } from "@app/helpers/validation/domain-validation"

type MyOrganizationMembership =
  GetMyOrganizationsQuery["myOrganizations"][number]

type OrganizationSwitcherItem = MyOrganizationMembership & {
  isActive: boolean
}

export type SwitcherItem = OrganizationSwitcherItem & { type: "organization" }

const REMEMBERED_ORG_DOMAIN_KEY = "REMEMBERED_ORG_DOMAIN"
const DEFAULT_POLLING_INTERVAL = 10000

export class OrganizationSwitcherService extends Service {
  public static readonly ID = "ORGANIZATION_SWITCHER_SERVICE"

  private myOrganizationsRef = ref<MyOrganizationMembership[]>([])

  // Starts true to prevent empty state flicker during auth init
  private isLoadingRef = ref(true)
  private errorRef = ref<string | null>(null)

  private rememberedOrgDomain = useLocalStorage<string | null>(
    REMEMBERED_ORG_DOMAIN_KEY,
    null
  )

  // Populated synchronously from localStorage during auth init
  private probableUser = useStreamStatic(
    platform.auth.getProbableUserStream(),
    platform.auth.getProbableUser(),
    () => {
      /* noop */
    }
  )[0]

  // Waits for Firebase auth token before populating
  private currentUser = useStreamStatic(
    platform.auth.getCurrentUserStream(),
    platform.auth.getCurrentUser(),
    () => {
      /* noop */
    }
  )[0]

  private pollingEnabled = ref(false)
  private pollingControl = useIntervalFn(
    () => this.fetchMyOrganizations(true),
    DEFAULT_POLLING_INTERVAL,
    { immediate: false }
  )
  private pausePolling = this.pollingControl.pause
  private resumePolling = this.pollingControl.resume

  public readonly myOrganizations = computed(
    () => this.myOrganizationsRef.value
  )

  public readonly isLoading = computed(() => this.isLoadingRef.value)

  public readonly error = computed(() => this.errorRef.value)

  public readonly currentOrgDomain = getOrganizationDomain()

  public readonly isOnDefaultInstance = isDefaultCloudInstance

  public readonly switcherItems = computed<SwitcherItem[]>(() => {
    const currentDomain = this.currentOrgDomain

    return this.myOrganizationsRef.value.map((membership) => ({
      type: "organization" as const,
      ...membership,
      isActive: membership.organization.orgDomain === currentDomain,
    }))
  })

  override async onServiceInit() {
    watch(
      this.currentUser,
      async (user) => {
        if (!user) {
          this.myOrganizationsRef.value = []

          // Check probableUser to prevent empty state flicker on hard reload
          if (!this.probableUser.value) {
            this.isLoadingRef.value = false
          }

          this.pausePolling()
          return
        }

        await this.fetchMyOrganizations()

        if (this.pollingEnabled.value) {
          this.resumePolling()
        }
      },
      { immediate: true }
    )
  }

  public async fetchMyOrganizations(silent = false) {
    if (!this.currentUser.value) {
      return
    }

    if (!silent) {
      this.isLoadingRef.value = true
    }
    this.errorRef.value = null

    try {
      await platform.auth.waitProbableLoginToConfirm()

      const result = await runGQLQuery<
        GetMyOrganizationsQuery,
        Record<string, never>,
        string
      >({
        query: GetMyOrganizationsDocument,
        variables: {},
      })

      if (E.isRight(result)) {
        this.myOrganizationsRef.value = result.right.myOrganizations || []
        this.errorRef.value = null
      } else {
        const errorMsg =
          result.left.error instanceof Error
            ? result.left.error.message
            : typeof result.left.error === "string"
              ? result.left.error
              : JSON.stringify(result.left.error)

        if (
          !errorMsg.includes("Unauthorized") &&
          !errorMsg.includes("Forbidden")
        ) {
          this.errorRef.value = errorMsg
        }
        console.error("Failed to fetch organizations:", result.left.error)
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unknown error"
      if (
        !errorMsg.includes("Unauthorized") &&
        !errorMsg.includes("Forbidden")
      ) {
        this.errorRef.value = errorMsg
      }
      console.error("Error fetching organizations:", err)
    } finally {
      this.isLoadingRef.value = false
    }
  }

  public async switchToOrganization(orgDomain: string | null) {
    if (orgDomain !== null) {
      const validationError = validateOrgDomain(orgDomain)
      if (validationError) {
        this.errorRef.value = validationError
        console.error(validationError)
        return
      }
    }

    this.rememberedOrgDomain.value = orgDomain
    if (platform.organization?.switchToInstance) {
      await platform.organization.switchToInstance(orgDomain)
      return
    }

    const rootDomain = platform.organization?.getRootDomain() ?? "hoppscotch.io"
    const targetUrl = orgDomain
      ? `${window.location.protocol}//${orgDomain}.${rootDomain}`
      : `${window.location.protocol}//${rootDomain}`

    window.location.href = targetUrl
  }

  public getRememberedOrgDomain(): string | null {
    return this.rememberedOrgDomain.value
  }

  public enablePolling() {
    this.pollingEnabled.value = true
    if (this.currentUser.value) {
      this.resumePolling()
    }
  }

  public disablePolling() {
    this.pollingEnabled.value = false
    this.pausePolling()
  }

  public hasOrganizations(): boolean {
    return this.myOrganizationsRef.value.length > 0
  }

  public isAdminOfCurrentOrg(): boolean {
    const currentDomain = this.currentOrgDomain
    if (!currentDomain) return false

    const membership = this.myOrganizationsRef.value.find(
      (m) => m.organization.orgDomain === currentDomain
    )

    return membership?.isAdmin ?? false
  }
}
